import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  GridItem,
  Heading,
  Stack,
  Text,
} from '@chakra-ui/react'
import { faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Cookie from 'js-cookie'
import React from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import 'swiper/swiper-bundle.css'

import { makeServerProps } from '@/server/query'

import makePage from '@/client/services/pages/makePage'
import {
  getBecomeATutorUrl,
  getDashboardUrl,
  getExploreUrl,
  getPartnerLogoURL,
  getSignUpUrl,
} from '@/client/services/url'
import { COOKIE_REFERRAL_CODE } from '@/client/settings'

import WhoWeAre from '@/components/About/WhoWeAre'
import AuthLink from '@/components/Auth/AuthLink'
import Logo from '@/components/Icons/Logo'
import KeyClubTutorathonLeaderboard from '@/components/KeyClub/KeyClubTutorathonLeaderboard'
import Section from '@/components/Landing/Section'
import Layout from '@/components/Layout/Layout'
import ShwGrid from '@/components/Layout/ShwGrid'
import OptimizedImage from '@/components/PublicImage/OptimizedImage'
import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'
import Tabs from '@/components/Tabs/Tabs'
import NumberedSteps from '@/components/UI/NumberedSteps'

const KEY_CLUB_REFERRAL_CODE = 'KEY_CLUB'
const FLYERS_LINK =
  'https://docs.google.com/document/d/11PJ7qRD4PefSrq3RkCO-vfmDNFJpVP012mEfsAG6WVY/edit?tab=t.0#heading=h.ttbeyhkia70z'
const PRESENTATION_LINK =
  'https://docs.google.com/presentation/d/1mnLvNZPZZEhBHVa_SOQvdVATNH0Nx0o1-rlmBtr7WMk/edit?usp=sharing'

const GET_INVOLVED_STEPS = [
  {
    key: 'get-started-step-1',
    name: () => (
      <Heading size="lg" as="h4" mb="16px">
        Create an account
      </Heading>
    ),
    description: () => (
      <Stack direction="column" spacing={6}>
        <Text>
          Create a Schoolhouse account and check out everything we have to offer, from live math
          homework and SAT help to small-group tutoring sessions. Everything is completely free!
        </Text>
        <Stack direction="row" spacing={2}>
          <ShwLink href={getSignUpUrl(null, null)}>
            <Button variant="outline">Create an Account</Button>
          </ShwLink>
          <ShwLink href={getExploreUrl()}>
            <Button variant="outline">Explore Sessions</Button>
          </ShwLink>
        </Stack>
      </Stack>
    ),
  },
  {
    key: 'get-started-step-2',
    name: () => (
      <Heading size="lg" as="h4" mb="16px">
        Become a certified tutor
      </Heading>
    ),
    description: () => (
      <Stack direction="column" spacing={6}>
        <Text>
          Interested in earning community service hours, from the convenience of your bedroom?
          Consider becoming a tutor! You&apos;ll have the opportunity to share your knowledge with
          the world, develop your leadership skills, and boost your college application.
        </Text>
        <ShwLink href={getBecomeATutorUrl()}>
          <Button variant="outline">Learn More</Button>
        </ShwLink>
      </Stack>
    ),
  },
  {
    key: 'get-started-step-3',
    name: () => (
      <Heading size="lg" as="h4" mb="16px">
        Become a student ambassador
      </Heading>
    ),
    description: () => (
      <Stack direction="column" spacing={6}>
        <Text>
          Share Schoolhouse with friends, family, teachers, and anyone else that might benefit.
          We&apos;ve put together some flyers to help you spread the word.
        </Text>
        <ShwLink href={FLYERS_LINK}>
          <Button variant="outline" rightIcon={<FontAwesomeIcon icon={faExternalLink} />}>
            Download Flyers
          </Button>
        </ShwLink>
      </Stack>
    ),
  },
]

const FACULTY_GET_INVOLVED_STEPS = [
  {
    key: 'fac-get-started-step-1',
    name: () => (
      <Heading size="lg" as="h4" mb="16px">
        Learn more about Schoolhouse
      </Heading>
    ),
    description: () => (
      <Stack direction="column" spacing={6}>
        <Text>
          Look over this presentation to learn more about Schoolhouse, our impact so far, and why we
          are a great match for Key Club members!
        </Text>
        <ShwLink href={PRESENTATION_LINK}>
          <Button variant="outline">Learn More</Button>
        </ShwLink>
      </Stack>
    ),
  },
  {
    key: 'fac-get-started-step-2',
    name: () => (
      <Heading size="lg" as="h4" mb="16px">
        Encourage Key Club members to become tutors
      </Heading>
    ),
    description: () => (
      <Stack direction="column" spacing={6}>
        <Text>
          Share Schoolhouse with your school&apos;s Key Club - we&apos;ve put together some flyers
          to help you spread the word! Help your students take advantage of this opportunity to
          share knowledge with the world, develop leadership skills, and boost college applications.
        </Text>
        <ShwLink href={FLYERS_LINK}>
          <Button variant="outline" rightIcon={<FontAwesomeIcon icon={faExternalLink} />}>
            Download Flyers
          </Button>
        </ShwLink>
      </Stack>
    ),
  },
  {
    key: 'fac-get-started-step-3',
    name: () => (
      <Heading size="lg" as="h4" mb="16px">
        Help pilot new initiatives
      </Heading>
    ),
    description: () => (
      <Stack direction="column" spacing={6}>
        <Text>
          Interested in getting more involved? Email us at partner@schoolhouse.world! Schoolhouse
          and Key Club are planning to try out new initiatives together this Fall and would love for
          your school to help us figure out what works best.
        </Text>
        <ShwLink href="mailto:partner@schoolhouse.world">
          <Button variant="outline" rightIcon={<FontAwesomeIcon icon={faExternalLink} />}>
            Get in Touch
          </Button>
        </ShwLink>
      </Stack>
    ),
  },
]

type StepsTab = 'For Students' | 'For Faculty Advisors'

const SHOW_TUTORATHON = true

const KeyClubIndex: React.FC = () => {
  React.useEffect(() => {
    Cookie.set(COOKIE_REFERRAL_CODE, KEY_CLUB_REFERRAL_CODE, {
      expires: 7,
      // ^ in days
      path: '/',
    })
  }, [])

  const [tabToShow, setTabToShow] = React.useState<StepsTab>('For Students')

  return (
    <Layout title="Key Club">
      <Flex alignItems="center">
        <Container>
          <ShwGrid alignItems="center" justifyItems="center">
            <GridItem
              colSpan={[12, 12, 12, 5]}
              marginTop={[0, 0, 0, '62px']}
              marginBottom={[0, 0, 0, '62px']}
            >
              <Center>
                <Box>
                  <Stack direction="row" spacing={5} mb={6} align="center">
                    <OptimizedImage
                      src={getPartnerLogoURL('key-club-seal.png')}
                      originalWidth={1330}
                      originalHeight={1321}
                      displayWidth="80px"
                      alt=""
                    />
                    <Box height="70px" borderRight={`4px solid ${chakraTokenToVar('border')}`} />
                    <Box ml={2} height="70px">
                      <Logo height="100%" width="auto" />
                    </Box>
                  </Stack>
                  <Heading size="3xl" as="h1" my="16px">
                    Welcome, Key Club members!
                  </Heading>
                  <Text my="24px" fontSize="16px">
                    Key Club has partnered with Schoolhouse to bring free tutoring to thousands of
                    learners across the world. If you&apos;re interested in learning something new,
                    or you&apos;re interested in becoming a tutor yourself, sign up today!
                  </Text>
                  <AuthLink mode="signup" returnTo={getDashboardUrl()}>
                    <Button variant="solid" width={['100%', 'auto']}>
                      Sign up
                    </Button>
                  </AuthLink>
                </Box>
              </Center>
            </GridItem>
            <GridItem colSpan={[12, 12, 12, 7]}>
              <Center>
                <PublicImage
                  src="/home/hero-v2.png"
                  alt="Join Schoolhouse for virtual, small-group tutoring in SAT® prep, math, and more!"
                  maxWidth="100%"
                />
              </Center>
            </GridItem>
          </ShwGrid>
        </Container>
      </Flex>
      {SHOW_TUTORATHON ? (
        <Section py={8} bg="bg.subtle">
          <KeyClubTutorathonLeaderboard />
        </Section>
      ) : null}
      <Section bg="primary.subtle" py={8}>
        <Center>
          <NumberedSteps
            title={() => (
              <Flex align="center" direction="column">
                <Box borderBottom="4px solid" mb={6} borderColor="yellow.border">
                  <Heading size="2xl">How to get involved</Heading>
                </Box>
                <Tabs<StepsTab>
                  variant="rounded"
                  tabs={['For Students', 'For Faculty Advisors']}
                  currentTab={tabToShow}
                  onTabChange={setTabToShow}
                  mb={2}
                  mt={4}
                />
              </Flex>
            )}
            steps={tabToShow === 'For Students' ? GET_INVOLVED_STEPS : FACULTY_GET_INVOLVED_STEPS}
            numberStyle={{ color: 'primary.fg' }}
          />
        </Center>
      </Section>
      <WhoWeAre />
    </Layout>
  )
}

const KeyClubIndexPage = makePage(KeyClubIndex, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(KeyClubIndexPage)
export default KeyClubIndexPage
